/**
 * AdminUsersFilterBar — Iter-290b Sprint C
 *
 * Owns:
 *   • Sort dropdown (#4)
 *   • Saved Views chip strip + manage (#5)
 *   • Advanced Filters dialog (#5)
 *
 * It is a controlled component: the parent Users.js page owns the canonical
 * filter state ({sort, advFilters, savedViewId}) and the fetch logic. This
 * component renders the UI and emits change events.
 *
 * Props:
 *   sort, onSortChange
 *   advFilters, onAdvFiltersChange
 *   savedViewId, onSavedViewChange  (id|null)
 *   currentRoleFilter             (string — needed to scope "talent-only" filters)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowDownAZ, ArrowUpAZ, ArrowDown01, ArrowUp10, Activity, Star, SlidersHorizontal, Bookmark, Plus, Trash2, Pencil, X, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

// Iter-292d — Predefined value lists for dropdown filters.
// Free-text remains for City, ZIP, Ethnicity, Language, Roles (CSVs / open
// taxonomies) and the global search bar (Name, Email, Company, Notes).
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];
const GENDER_OPTIONS = [
  { value: 'female',         label: 'Female' },
  { value: 'male',           label: 'Male' },
  { value: 'non-binary',     label: 'Non-binary' },
  { value: 'prefer_not_say', label: 'Prefer not to say' },
];
const BG_CHECK_OPTIONS = [
  { value: 'cleared',     label: 'Cleared' },
  { value: 'pending',     label: 'Pending' },
  { value: 'failed',      label: 'Failed' },
  { value: 'not_started', label: 'Not started' },
];

export const SORT_OPTIONS = [
  { id: 'first_name_asc',  label: 'First Name (A → Z)',  Icon: ArrowDownAZ },
  { id: 'first_name_desc', label: 'First Name (Z → A)',  Icon: ArrowUpAZ },
  { id: 'last_name_asc',   label: 'Last Name (A → Z)',   Icon: ArrowDownAZ },
  { id: 'last_name_desc',  label: 'Last Name (Z → A)',   Icon: ArrowUpAZ },
  { id: 'joined_newest',   label: 'Date Joined (Newest)', Icon: ArrowUp10 },
  { id: 'joined_oldest',   label: 'Date Joined (Oldest)', Icon: ArrowDown01 },
  { id: 'recently_active', label: 'Recently Active',     Icon: Activity },
];

const EMPTY_FILTERS = {
  state: '', city: '', zip: '',
  gender: '', ethnicity: '', language: '',
  age_min: '', age_max: '',
  min_rating: '', min_completed_events: '', max_no_shows: '',
  verified_only: false, background_check_status: '',
  has_portfolio_photos: '', has_resume: '', has_certifications: '',
  roles: '', incomplete_profile: false, joined_within_days: '',
};

function activeFilterCount(f) {
  return Object.entries(f || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined && v !== false).length;
}

export default function AdminUsersFilterBar({
  sort, onSortChange,
  advFilters, onAdvFiltersChange,
  savedViewId, onSavedViewChange,
  currentRoleFilter = 'all',
  // Iter-292d — Export CSV (next to "Save view").
  onExportCsv,
  exporting = false,
  // Iter-292h — Default sort behaviour. Parent passes the canonical
  // default; the "Clear" button resets sort to this value alongside
  // dropping all advanced filters and the saved-view selection.
  defaultSort = 'joined_newest',
  onResetToDefault,
}) {
  const [views, setViews] = useState([]);
  const [showAdv, setShowAdv] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [draft, setDraft] = useState(advFilters || EMPTY_FILTERS);

  const loadViews = useCallback(async () => {
    try {
      const res = await api.get('/admin/saved-views');
      setViews(res.data?.views || []);
    } catch (e) {
      // non-fatal
    }
  }, []);
  useEffect(() => { loadViews(); }, [loadViews]);
  useEffect(() => { setDraft(advFilters || EMPTY_FILTERS); }, [advFilters]);

  const handleApplyView = async (view) => {
    onSavedViewChange(view.id);
    onSortChange(view.sort_by || defaultSort);  // Iter-292h — fall back to "Newest" default
    // Project the view's filters onto advFilters; role + status are owned by
    // Users.js parent already, but we still forward them through filters.
    onAdvFiltersChange({
      ...EMPTY_FILTERS,
      ...view.filters,
      verified_only: view.filters?.verified_only === true,
      incomplete_profile: view.filters?.incomplete_profile === true,
    });
  };

  const handleSaveCurrent = async () => {
    if (!saveName.trim()) { toast.error('Pick a name'); return; }
    // Filter out empties from draft before saving
    const cleaned = Object.fromEntries(
      Object.entries(draft).filter(([, v]) => v !== '' && v !== null && v !== undefined && v !== false)
    );
    if (currentRoleFilter && currentRoleFilter !== 'all') cleaned.role = currentRoleFilter;
    try {
      await api.post('/admin/saved-views', {
        name: saveName.trim(),
        description: saveDescription.trim() || null,
        filters: cleaned,
        sort_by: sort || null,
      });
      toast.success('Saved view created');
      setSaveName(''); setSaveDescription('');
      setShowSave(false);
      await loadViews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save view');
    }
  };

  const handleDeleteView = async (view) => {
    if (view.is_global) { toast.error('Default views cannot be deleted'); return; }
    if (!window.confirm(`Delete saved view "${view.name}"?`)) return;
    try {
      await api.delete(`/admin/saved-views/${view.id}`);
      toast.success('View deleted');
      if (savedViewId === view.id) onSavedViewChange(null);
      await loadViews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const activeCount = activeFilterCount(advFilters);

  return (
    <div className="space-y-3" data-testid="admin-users-filter-bar">
      {/* Top toolbar: sort + advanced + save */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs text-slate-500 hidden sm:inline">Sort</Label>
          <Select value={sort || ''} onValueChange={(v) => onSortChange(v)}>
            <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="users-sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(({ id, label }) => (
                <SelectItem key={id} value={id} data-testid={`users-sort-${id}`}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowAdv(true)}
          data-testid="users-open-adv-filters"
        >
          <SlidersHorizontal className="w-4 h-4 mr-1.5" />
          More filters
          {activeCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-800">
              {activeCount}
            </span>
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowSave(true)}
          data-testid="users-save-view"
        >
          <Bookmark className="w-4 h-4 mr-1.5" /> Save view
        </Button>
        {/* Iter-292d — Export the current view (filters + sort) as CSV. */}
        {onExportCsv && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onExportCsv}
            disabled={exporting}
            data-testid="users-export-csv"
            title="Download the currently filtered users as a CSV (capped at 5,000 rows)"
          >
            {exporting
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Download className="w-4 h-4 mr-1.5" />}
            Export CSV
          </Button>
        )}
        {activeCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              // Iter-292h — "Clear" now ALSO resets sort to the
              // default ("Newest Talent First") so the page returns
              // to its canonical state.
              onAdvFiltersChange(EMPTY_FILTERS);
              onSavedViewChange(null);
              if (onResetToDefault) onResetToDefault();
              else if (sort !== defaultSort) onSortChange(defaultSort);
            }}
            data-testid="users-clear-filters"
            className="text-slate-600"
          >
            <X className="w-4 h-4 mr-1.5" /> Clear
          </Button>
        )}
      </div>

      {/* Saved view chips */}
      {views.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="users-saved-views-bar">
          {views.map((v) => {
            const active = savedViewId === v.id;
            return (
              <div key={v.id} className="inline-flex">
                <button
                  type="button"
                  onClick={() => handleApplyView(v)}
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${active ? 'bg-cyan-600 border-cyan-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                  title={v.description || ''}
                  data-testid={`saved-view-chip-${v.id}`}
                >
                  {v.is_global ? <Star className="w-3 h-3" /> : <Bookmark className="w-3 h-3" />}
                  {v.name}
                </button>
                {!v.is_global && (
                  <button
                    type="button"
                    onClick={() => handleDeleteView(v)}
                    className="-ml-1 px-1.5 py-1 text-slate-400 hover:text-rose-600"
                    aria-label={`Delete ${v.name}`}
                    data-testid={`saved-view-delete-${v.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Advanced filter dialog */}
      <Dialog open={showAdv} onOpenChange={setShowAdv}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="adv-filters-dialog">
          <DialogHeader>
            <DialogTitle>Advanced filters</DialogTitle>
            <DialogDescription>Filter talent by location, profile, experience, performance, and account status.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 py-2">
            <FieldSection title="Location">
              <FieldRow>
                <Label className="text-xs">State</Label>
                <Select value={draft.state || 'any'} onValueChange={(v) => setDraft({ ...draft, state: v === 'any' ? '' : v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="adv-state">
                    <SelectValue placeholder="Any state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="any">Any state</SelectItem>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow><Label className="text-xs">City</Label><Input value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} placeholder="Atlanta" data-testid="adv-city" /></FieldRow>
              <FieldRow><Label className="text-xs">ZIP</Label><Input value={draft.zip} onChange={(e) => setDraft({ ...draft, zip: e.target.value })} placeholder="30303" data-testid="adv-zip" /></FieldRow>
            </FieldSection>
            <FieldSection title="Profile">
              <FieldRow>
                <Label className="text-xs">Gender</Label>
                <Select value={draft.gender || 'any'} onValueChange={(v) => setDraft({ ...draft, gender: v === 'any' ? '' : v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="adv-gender">
                    <SelectValue placeholder="Any gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any gender</SelectItem>
                    {GENDER_OPTIONS.map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow><Label className="text-xs">Ethnicity (CSV)</Label><Input value={draft.ethnicity} onChange={(e) => setDraft({ ...draft, ethnicity: e.target.value })} placeholder="Latino, Asian" data-testid="adv-ethnicity" /></FieldRow>
              <FieldRow><Label className="text-xs">Language (CSV)</Label><Input value={draft.language} onChange={(e) => setDraft({ ...draft, language: e.target.value })} placeholder="Spanish, French" data-testid="adv-language" /></FieldRow>
              <FieldRow>
                <Label className="text-xs">Age range</Label>
                <div className="flex gap-2">
                  <Input value={draft.age_min} onChange={(e) => setDraft({ ...draft, age_min: e.target.value })} placeholder="min" type="number" data-testid="adv-age-min" />
                  <Input value={draft.age_max} onChange={(e) => setDraft({ ...draft, age_max: e.target.value })} placeholder="max" type="number" data-testid="adv-age-max" />
                </div>
              </FieldRow>
            </FieldSection>
            <FieldSection title="Experience">
              <FieldRow><Label className="text-xs">Roles (CSV)</Label><Input value={draft.roles} onChange={(e) => setDraft({ ...draft, roles: e.target.value })} placeholder="Brand Ambassador, Team Captain" data-testid="adv-roles" /></FieldRow>
              <FieldRow>
                <Label className="text-xs">Assets</Label>
                <div className="flex flex-wrap gap-3 text-xs">
                  <BoolFilter label="Has portfolio photos" value={draft.has_portfolio_photos} onChange={(v) => setDraft({ ...draft, has_portfolio_photos: v })} testid="adv-has-portfolio" />
                  <BoolFilter label="Has resume" value={draft.has_resume} onChange={(v) => setDraft({ ...draft, has_resume: v })} testid="adv-has-resume" />
                  <BoolFilter label="Has certifications" value={draft.has_certifications} onChange={(v) => setDraft({ ...draft, has_certifications: v })} testid="adv-has-certs" />
                </div>
              </FieldRow>
            </FieldSection>
            <FieldSection title="Performance">
              <FieldRow><Label className="text-xs">Min average rating</Label><Input value={draft.min_rating} onChange={(e) => setDraft({ ...draft, min_rating: e.target.value })} placeholder="4.5" type="number" step="0.1" data-testid="adv-min-rating" /></FieldRow>
              <FieldRow><Label className="text-xs">Min completed events</Label><Input value={draft.min_completed_events} onChange={(e) => setDraft({ ...draft, min_completed_events: e.target.value })} placeholder="5" type="number" data-testid="adv-min-events" /></FieldRow>
              <FieldRow><Label className="text-xs">Max no-shows</Label><Input value={draft.max_no_shows} onChange={(e) => setDraft({ ...draft, max_no_shows: e.target.value })} placeholder="0" type="number" data-testid="adv-max-noshow" /></FieldRow>
              <FieldRow>
                <Label className="text-xs">Verified / BG-check</Label>
                <div className="flex items-center gap-3 flex-wrap text-xs">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={!!draft.verified_only} onChange={(e) => setDraft({ ...draft, verified_only: e.target.checked })} data-testid="adv-verified-only" />
                    Verified only
                  </label>
                  <Select value={draft.background_check_status || 'any'} onValueChange={(v) => setDraft({ ...draft, background_check_status: v === 'any' ? '' : v })}>
                    <SelectTrigger className="h-8 text-xs flex-1 min-w-[140px]" data-testid="adv-bgcheck">
                      <SelectValue placeholder="Background check" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">BG check: Any</SelectItem>
                      {BG_CHECK_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </FieldRow>
            </FieldSection>
            <FieldSection title="Account">
              <FieldRow>
                <Label className="text-xs">Joined within last (days)</Label>
                <Input value={draft.joined_within_days} onChange={(e) => setDraft({ ...draft, joined_within_days: e.target.value })} placeholder="30" type="number" data-testid="adv-joined-within" />
              </FieldRow>
              <FieldRow>
                <Label className="text-xs">Incomplete profile</Label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs">
                  <input type="checkbox" checked={!!draft.incomplete_profile} onChange={(e) => setDraft({ ...draft, incomplete_profile: e.target.checked })} data-testid="adv-incomplete-profile" />
                  Show only incomplete profiles (&lt;80%)
                </label>
              </FieldRow>
            </FieldSection>
          </div>
          <DialogFooter className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => { setDraft(EMPTY_FILTERS); }} data-testid="adv-filters-reset">Reset</Button>
            <Button variant="outline" onClick={() => setShowAdv(false)} data-testid="adv-filters-cancel">Cancel</Button>
            <Button onClick={() => { onAdvFiltersChange(draft); setShowAdv(false); }} data-testid="adv-filters-apply">Apply filters</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save current view */}
      <Dialog open={showSave} onOpenChange={setShowSave}>
        <DialogContent data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle>Save current view</DialogTitle>
            <DialogDescription>Save the current sort + filters as a reusable view.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <FieldRow><Label className="text-xs">Name</Label><Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g., NYC Hosts (high rated)" data-testid="save-view-name" /></FieldRow>
            <FieldRow><Label className="text-xs">Description (optional)</Label><Input value={saveDescription} onChange={(e) => setSaveDescription(e.target.value)} placeholder="Description" data-testid="save-view-description" /></FieldRow>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSave(false)} data-testid="save-view-cancel">Cancel</Button>
            <Button onClick={handleSaveCurrent} data-testid="save-view-submit"><Plus className="w-4 h-4 mr-1.5" /> Create view</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldSection({ title, children }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{title}</div>
      {children}
    </div>
  );
}
function FieldRow({ children }) {
  return <div className="grid grid-cols-1 gap-1">{children}</div>;
}
function BoolFilter({ label, value, onChange, testid }) {
  return (
    <Select value={value === true ? 'yes' : value === false ? 'no' : 'any'} onValueChange={(v) => onChange(v === 'any' ? '' : v === 'yes')}>
      <SelectTrigger className="w-auto h-8 text-xs" data-testid={testid}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">{label}: Any</SelectItem>
        <SelectItem value="yes">{label}: Yes</SelectItem>
        <SelectItem value="no">{label}: No</SelectItem>
      </SelectContent>
    </Select>
  );
}
